import React, { useState } from 'react';
import { Colors } from '../../theme';
import { formatE164USPhoneNumber, formatPhoneAsYouType, stripPhoneNumber } from 'shared';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import CancelIcon from '@mui/icons-material/Cancel';

interface IPhoneProps {
    id?: string;
    label: string;
    onChange: (value: string, display: string, isValid: boolean) => void;
    value: string;
    error: boolean;
    errorText: string;
}

const styles = {
    label: {
        fontFamily: 'Outfit',
        color: Colors.darkNavy,
        lineHeight: '1.2em',
        minHeight: '1.2em',
        display: 'block',
    },
    errorText: {
        fontFamily: 'Outfit',
        color: Colors.mediumRed,
        fontSize: 12,
        margin: 0,
    },
};

const StyledInput = styled('input')({
    '&:hover': {
        outline: 'none',
    },
    '&:focus': {
        outline: 'none',
    },
    '&::placeholder': {
        color: Colors.mediumNavy,
    },
    borderBottomColor: Colors.mediumNavy,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderWidth: 0,
    borderBottomWidth: 1,
    width: '100%',
    autoCapitalize: 'none',
    paddingRight: 36,
});

export default function PhoneNumberInput({ label, onChange, value, error, errorText, id }: IPhoneProps) {
    const [lastPhone, setLastPhone] = useState<string>('');
    const [touched, setTouched] = useState(false);

    function getPlaceholder() {
        return value ? '' : label;
    }

    const format = (val: string) => {
        const digitsOnly = stripPhoneNumber(val);
        const newValue = formatPhoneAsYouType(digitsOnly);
        const returnVal = newValue === lastPhone ? newValue.slice(0, -1) : newValue;
        setLastPhone(returnVal);
        return returnVal;
    };

    function onPhoneChange(text: string) {
        const digitsOnly = stripPhoneNumber(text);
        const phone = formatE164USPhoneNumber(digitsOnly);
        const display = format(digitsOnly);
        const isValid = !!digitsOnly && isValidPhoneNumber(phone.toString(), 'US');
        onChange(phone.toString(), display, isValid);
    }

    return (
        <Grid container direction="column">
            <Grid item>
                <label style={{ ...styles.label, visibility: value ? 'visible' : 'hidden' }}>{label}</label>
            </Grid>
            <Grid item>
                <StyledInput
                    id={id}
                    type="tel"
                    value={value}
                    onChange={(e) => onPhoneChange(e.target.value)}
                    onBlur={() => setTouched(true)}
                    placeholder={getPlaceholder()}
                    style={{ color: Colors.darkNavy }}
                />
            </Grid>
            {touched && error ? (
                <Grid container item alignItems="center" style={{ paddingTop: 5 }}>
                    <Grid
                        item
                        style={{
                            paddingLeft: 5,
                            display: 'flex',
                            alignItems: 'center',
                        }}
                    >
                        <CancelIcon style={{ color: Colors.mediumRed, fontSize: 14, marginRight: 4 }} />
                        <p style={styles.errorText}>{errorText}</p>
                    </Grid>
                </Grid>
            ) : null}
        </Grid>
    );
}
